/**
 * 媒体数据契约层
 * 单一权威的媒体数据处理入口
 */

import { MediaCacheStore } from "./mediaCacheStore";

// ============ 类型定义 ============

/** 缓存引用 */
export interface MediaCacheRef {
  cacheId: string;
  status: "pending" | "cached" | "failed";
  mimeType?: string;
}

/** 上传文件项（表单格式） */
export interface UploadFileItem {
  id?: string;
  file?: File | Blob | null;
  src?: string | null;
  cacheRef?: MediaCacheRef;
}

/** 标准化媒体项 */
export interface NormalizedMediaItem {
  id?: string;
  source:
    | { type: "file"; file: File | Blob }
    | { type: "url"; url: string }
    | { type: "cache"; ref: MediaCacheRef };
}

/** 解析后的媒体项 */
export interface ResolvedMediaItem {
  id?: string;
  file?: File | Blob;
  url?: string;
  mimeType?: string;
  /** 标记此 URL 是否为 Object URL（需要手动释放） */
  isObjectUrl?: boolean;
}

/** 解析选项 */
export interface ResolveOptions {
  /** 是否下载 URL 为本地 Blob（用于需要 base64 的场景） */
  downloadUrls?: boolean;
  /** 最大并发数 */
  concurrency?: number;
  /** 单项超时时间 (ms) */
  timeout?: number;
}

// ============ 类型守卫 ============

export const isMediaCacheRef = (value: unknown): value is MediaCacheRef => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return typeof obj.cacheId === "string" && typeof obj.status === "string";
};

export const isUploadFileItem = (value: unknown): value is UploadFileItem => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return "file" in obj || "src" in obj || "id" in obj || "cacheRef" in obj;
};

// ============ 规范化（同步） ============

/**
 * 将任意媒体输入规范化为标准格式
 * 这是媒体处理的唯一入口点
 */
export const normalizeMediaInput = (
  value: unknown,
  maxItems?: number
): NormalizedMediaItem[] => {
  const results: NormalizedMediaItem[] = [];

  const processItem = (item: unknown): void => {
    if (maxItems !== undefined && results.length >= maxItems) return;

    // 1. 纯字符串 URL
    if (typeof item === "string" && item.trim()) {
      results.push({ source: { type: "url", url: item.trim() } });
      return;
    }

    // 2. File/Blob 对象
    if (item instanceof File || item instanceof Blob) {
      results.push({ source: { type: "file", file: item } });
      return;
    }

    // 3. 缓存引用
    if (isMediaCacheRef(item)) {
      results.push({ source: { type: "cache", ref: item } });
      return;
    }

    // 4. UploadFileItem 对象
    if (isUploadFileItem(item)) {
      if (item.file) {
        results.push({ id: item.id, source: { type: "file", file: item.file } });
      } else if (item.cacheRef) {
        results.push({ id: item.id, source: { type: "cache", ref: item.cacheRef } });
      } else if (item.src?.trim()) {
        results.push({ id: item.id, source: { type: "url", url: item.src.trim() } });
      }
      return;
    }

    // 5. 数组递归
    if (Array.isArray(item)) {
      for (const subItem of item) {
        processItem(subItem);
      }
    }
  };

  processItem(value);
  return results;
};

// ============ 解析（异步） ============

/**
 * 解析单个标准化媒体项
 * 将 cacheRef 转换为实际的 File/Blob
 */
export const resolveMediaItem = async (
  item: NormalizedMediaItem
): Promise<ResolvedMediaItem | null> => {
  const { source } = item;

  switch (source.type) {
    case "file":
      return {
        id: item.id,
        file: source.file,
        mimeType: source.file.type || undefined,
      };

    case "url":
      return { id: item.id, url: source.url };

    case "cache": {
      if (source.ref.status !== "cached") return null;
      try {
        const entry = await MediaCacheStore.get(source.ref.cacheId);
        if (entry?.data) {
          return {
            id: item.id,
            file: entry.data,
            url: URL.createObjectURL(entry.data),
            mimeType: entry.mimeType || source.ref.mimeType,
            isObjectUrl: true,
          };
        }
      } catch (error) {
        console.warn("[resolveMediaItem] 缓存解析失败:", source.ref.cacheId, error);
      }
      return null;
    }
  }
};

/**
 * 释放 ResolvedMediaItem 中的 Object URL
 * 调用方负责在不再需要时调用此函数
 */
export const revokeResolvedMediaItem = (item: ResolvedMediaItem | null | undefined): void => {
  if (item?.isObjectUrl && item.url) {
    try {
      URL.revokeObjectURL(item.url);
    } catch {
      // ignore
    }
  }
};

/**
 * 批量释放 ResolvedMediaItem 中的 Object URL
 */
export const revokeAllResolvedMediaItems = (items: Array<ResolvedMediaItem | null | undefined>): void => {
  for (const item of items) {
    revokeResolvedMediaItem(item);
  }
};

/** 批量解析（带选项） */
export const resolveAllMediaItems = async (
  items: NormalizedMediaItem[],
  options?: ResolveOptions
): Promise<ResolvedMediaItem[]> => {
  const { downloadUrls = false, timeout = 30000 } = options ?? {};

  const resolveOne = async (item: NormalizedMediaItem): Promise<ResolvedMediaItem | null> => {
    const { source } = item;

    switch (source.type) {
      case "file":
        return {
          id: item.id,
          file: source.file,
          mimeType: source.file.type || undefined,
        };

      case "url":
        if (downloadUrls) {
          // 下载 URL 为 Blob
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);
            const response = await fetch(source.url, { signal: controller.signal });
            clearTimeout(timeoutId);
            if (!response.ok) return { id: item.id, url: source.url };
            const blob = await response.blob();
            return {
              id: item.id,
              file: blob,
              url: source.url,
              mimeType: blob.type || undefined,
            };
          } catch {
            return { id: item.id, url: source.url };
          }
        }
        return { id: item.id, url: source.url };

      case "cache": {
        if (source.ref.status !== "cached") return null;
        try {
          const entry = await MediaCacheStore.get(source.ref.cacheId);
          if (entry?.data) {
            return {
              id: item.id,
              file: entry.data,
              url: URL.createObjectURL(entry.data),
              mimeType: entry.mimeType || source.ref.mimeType,
              isObjectUrl: true,
            };
          }
        } catch (error) {
          console.warn("[resolveMediaItem] 缓存解析失败:", source.ref.cacheId, error);
        }
        return null;
      }

      default: {
        // 穷尽性检查
        const _exhaustive: never = source;
        console.warn(`未知的媒体源类型: ${(_exhaustive as { type: string }).type}`);
        return null;
      }
    }
  };

  const results = await Promise.all(items.map(resolveOne));
  return results.filter((r): r is ResolvedMediaItem => r !== null);
};

// ============ 便捷方法 ============

/** 提取第一个文件 */
export const extractFirstFile = async (value: unknown): Promise<File | Blob | undefined> => {
  const items = normalizeMediaInput(value, 1);
  if (items.length === 0) return undefined;
  const resolved = await resolveMediaItem(items[0]);
  return resolved?.file;
};

/** 提取所有文件 */
export const extractAllFiles = async (value: unknown): Promise<Array<File | Blob>> => {
  const items = normalizeMediaInput(value);
  const resolved = await resolveAllMediaItems(items);
  return resolved.map((r) => r.file).filter((f): f is File | Blob => !!f);
};

/** 提取第一个 URL */
export const extractFirstUrl = async (value: unknown): Promise<string | undefined> => {
  const items = normalizeMediaInput(value, 1);
  if (items.length === 0) return undefined;
  const resolved = await resolveMediaItem(items[0]);
  return resolved?.url;
};

/** 提取所有 URL */
export const extractAllUrls = async (value: unknown): Promise<string[]> => {
  const items = normalizeMediaInput(value);
  const resolved = await resolveAllMediaItems(items);
  return resolved.map((r) => r.url).filter((u): u is string => !!u);
};

/** 提取第一个文件或 URL（优先文件） */
export const extractFirstFileOrUrl = async (
  value: unknown
): Promise<File | Blob | string | undefined> => {
  const items = normalizeMediaInput(value, 1);
  if (items.length === 0) return undefined;
  const resolved = await resolveMediaItem(items[0]);
  if (!resolved) return undefined;
  return resolved.file ?? resolved.url;
};
